import React, { useState, useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { last, noop, get } from 'lodash';
import { FocusableParent } from 'components/navigation';
import { DetailsObjectType } from 'store/pages/loaders/loader-types';
import { StyledBackButton } from './StyledComponents';
import DetailsHeader from './DetailsHeader';
import WatchControls from './WatchControls';
import DetailsDescription from './DetailsDescription';

interface IMainContentSection {
  data: DetailsObjectType;
  pageOffset: number;
  baseFocusOffset: number;
  onItemFocus: (offset: number) => void;
  onPlay: () => void;
  onFavorite: () => void;
}

const SECTION_NAME = 'MAIN_CONTENT_SECTION';
const innerSectionsExtraOffset = [0, 540];
const DESCRIPTION_TEXT_MAX_LENGTH = 350;

const MainContentSection: React.FC<IMainContentSection> = (props) => {
  const { data, pageOffset, baseFocusOffset, onPlay, onFavorite, onItemFocus } = props;
  const history = useHistory();
  const [selectedSection, setSelectedSection] = useState(0);
  const isExpandDescriptionButtonVisible = useMemo(
    () => get(data.description, ['length'], 0) > DESCRIPTION_TEXT_MAX_LENGTH,
    [data],
  );

  const onInnerSectionFocus = useCallback(
    (index) => {
      const offset = innerSectionsExtraOffset[index] + baseFocusOffset;
      setSelectedSection(index);
      onItemFocus(offset);
    },
    [baseFocusOffset, onItemFocus],
  );

  const selectFirstSection = useCallback(() => onInnerSectionFocus(0), [onInnerSectionFocus]);
  const selectSecondSection = useCallback(() => onInnerSectionFocus(1), [onInnerSectionFocus]);

  const IsSectionCompletelyScrolled = pageOffset > baseFocusOffset + last(innerSectionsExtraOffset);

  return (
    <FocusableParent id={SECTION_NAME} sectionName={SECTION_NAME}>
      <StyledBackButton
        onEnter={history.goBack}
        onFocus={selectFirstSection}
        buttonOffset={pageOffset}
      />
      <DetailsHeader
        data={data}
        selectedSection={selectedSection}
        IsSectionCompletelyScrolled={IsSectionCompletelyScrolled}
        onCoverFocus={selectSecondSection}
        onCoverEnter={data ? onPlay : noop}
      />
      <WatchControls
        data={data || {}}
        sectionName="WATCH_CONTROLS"
        isVisible={selectedSection === 0}
        isExpandDescriptionButtonVisible={isExpandDescriptionButtonVisible}
        onFocus={selectFirstSection}
        onPlay={data ? onPlay : noop}
        onFavorite={onFavorite}
      />
      {data && (
        <DetailsDescription
          data={data}
          maxTextLength={DESCRIPTION_TEXT_MAX_LENGTH}
          isVisible={!IsSectionCompletelyScrolled}
          onFocus={selectSecondSection}
        />
      )}
    </FocusableParent>
  );
};

export default MainContentSection;
